import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  Mail, Plus, Calendar, AlertTriangle, ArrowDownLeft, ArrowUpRight,
  Edit, Trash2, Loader2, Search, Clock, User, Flag, FileText, Download, Send, PenLine, X, Paperclip
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const PRIORITIES = ['Low', 'Normal', 'High', 'Urgent'];
const CATEGORIES = ['General', 'Legal', 'HR', 'Finance', 'Operations', 'Customer', 'Supplier'];
const CLOSINGS = ['Yours faithfully', 'Yours sincerely', 'Best regards', 'Kind regards', 'Regards', 'Respectfully'];

// Quill editor configuration
const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'indent': '-1'}, { 'indent': '+1' }],
    [{ 'align': [] }],
    ['clean']
  ],
};

const quillFormats = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'list', 'bullet', 'indent',
  'align'
];

const LettersLedger = () => {
  const [letters, setLetters] = useState([]);
  const [pendingLetters, setPendingLetters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [editLetter, setEditLetter] = useState(null);
  const [editingComposedLetter, setEditingComposedLetter] = useState(null); // Track editing composed letter
  const [searchQuery, setSearchQuery] = useState('');
  const [directionFilter, setDirectionFilter] = useState('all');
  const [formData, setFormData] = useState({
    reference_number: '', subject: '', direction: 'Incoming', sender: '', recipient: '',
    letter_date: new Date().toISOString().split('T')[0], received_date: '', response_due_date: '',
    summary: '', category: 'General', priority: 'Normal', notes: ''
  });
  const [composeData, setComposeData] = useState({
    letter_date: new Date().toISOString().split('T')[0],
    recipient_name: '',
    recipient_address: '',
    salutation: 'Dear Sir/Madam',
    subject: '',
    content: '',
    closing_line: 'Yours faithfully',
    written_by: '',
    writer_title: '',
    category: 'General',
    priority: 'Normal'
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const [attachmentFile, setAttachmentFile] = useState(null);

  const { user, getAuthHeaders, currentOffice } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchLetters = async () => {
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (directionFilter !== 'all') params.append('direction', directionFilter);
      
      const response = await fetch(`${API_URL}/api/letters/?${params}`, { headers });
      if (response.ok) setLetters(await response.json());
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchPending = async () => {
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      const response = await fetch(`${API_URL}/api/letters/pending-response`, { headers });
      if (response.ok) setPendingLetters(await response.json());
    } catch (error) {
      console.error('Error:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchLetters(), fetchPending()]);
      setLoading(false);
    };
    if (currentOffice) loadData();
  }, [currentOffice, searchQuery, directionFilter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const url = editLetter ? `${API_URL}/api/letters/${editLetter.id}` : `${API_URL}/api/letters/`;
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      
      // Clean payload: convert empty strings to null for optional date fields
      const cleanData = { ...formData };
      if (!cleanData.received_date) cleanData.received_date = null;
      if (!cleanData.response_due_date) cleanData.response_due_date = null;
      if (!cleanData.summary) cleanData.summary = null;
      if (!cleanData.notes) cleanData.notes = null;
      
      const payload = editLetter ? cleanData : { ...cleanData, office_id: currentOffice?.id };
      
      const response = await fetch(url, {
        method: editLetter ? 'PUT' : 'POST',
        headers,
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const letterResult = await response.json();
        
        // Upload attachment if file was selected (only on create, not edit)
        if (attachmentFile && !editLetter && letterResult.id) {
          try {
            const uploadHeaders = getAuthHeaders();
            if (currentOffice?.id) uploadHeaders['X-Office-ID'] = currentOffice.id;
            // Remove Content-Type so browser sets multipart boundary
            delete uploadHeaders['Content-Type'];
            
            const formPayload = new FormData();
            formPayload.append('file', attachmentFile);
            
            await fetch(`${API_URL}/api/letters/${letterResult.id}/upload-attachment`, {
              method: 'POST',
              headers: uploadHeaders,
              body: formPayload
            });
          } catch (uploadErr) {
            console.error('Attachment upload error:', uploadErr);
            toast.error('Letter created but attachment upload failed');
          }
        }
        
        toast.success(editLetter ? 'Letter updated successfully' : 'Letter created successfully');
        setShowModal(false);
        setEditLetter(null);
        setAttachmentFile(null);
        resetForm();
        await Promise.all([fetchLetters(), fetchPending()]);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Operation failed');
      }
    } catch (error) {
      console.error('Submit error:', error);
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this letter?')) return;
    try {
      await fetch(`${API_URL}/api/letters/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
      await fetchLetters();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleMarkResponded = async (id) => {
    try {
      await fetch(`${API_URL}/api/letters/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status: 'Responded', responded_date: new Date().toISOString().split('T')[0] })
      });
      await Promise.all([fetchLetters(), fetchPending()]);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const openEditModal = (letter) => {
    setEditLetter(letter);
    setFormData({
      reference_number: letter.reference_number,
      subject: letter.subject,
      direction: letter.direction,
      sender: letter.sender,
      recipient: letter.recipient,
      letter_date: letter.letter_date,
      received_date: letter.received_date || '',
      response_due_date: letter.response_due_date || '',
      summary: letter.summary || '',
      category: letter.category || 'General',
      priority: letter.priority,
      notes: letter.notes || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      reference_number: '', subject: '', direction: 'Incoming', sender: '', recipient: '',
      letter_date: new Date().toISOString().split('T')[0], received_date: '', response_due_date: '',
      summary: '', category: 'General', priority: 'Normal', notes: ''
    });
    setAttachmentFile(null);
  };

  const resetComposeForm = () => {
    setComposeData({
      letter_date: new Date().toISOString().split('T')[0],
      recipient_name: '',
      recipient_address: '',
      salutation: 'Dear Sir/Madam',
      subject: '',
      content: '',
      closing_line: 'Yours faithfully',
      written_by: user?.name || '',
      writer_title: '',
      category: 'General',
      priority: 'Normal'
    });
    setEditingComposedLetter(null);
  };

  // Open compose modal for editing an existing composed letter
  const openEditComposeModal = (letter) => {
    setEditingComposedLetter(letter);
    setComposeData({
      letter_date: letter.letter_date || new Date().toISOString().split('T')[0],
      recipient_name: letter.recipient_name || letter.recipient || '',
      recipient_address: letter.recipient_address || '',
      salutation: letter.salutation || 'Dear Sir/Madam',
      subject: letter.subject || '',
      content: letter.content || letter.body || '',
      closing_line: letter.closing_line || 'Yours faithfully',
      written_by: letter.written_by || letter.author_name || '',
      writer_title: letter.writer_title || letter.author_title || '',
      category: letter.category || 'General',
      priority: letter.priority || 'Normal'
    });
    setShowComposeModal(true);
  };

  const handleComposeLetter = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      
      // If editing an existing letter, use PUT; otherwise use POST
      const isEditing = !!editingComposedLetter;
      const url = isEditing 
        ? `${API_URL}/api/letters/${editingComposedLetter.id}`
        : `${API_URL}/api/letters/compose`;
      
      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers,
        body: JSON.stringify(composeData)
      });

      if (response.ok) {
        const savedLetter = await response.json();
        toast.success(isEditing 
          ? 'Letter updated successfully' 
          : `Letter created with reference: ${savedLetter.reference_number}`);
        setShowComposeModal(false);
        resetComposeForm();
        await Promise.all([fetchLetters(), fetchPending()]);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || (isEditing ? 'Failed to update letter' : 'Failed to compose letter'));
      }
    } catch (error) {
      setError('Network error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadPdf = async (letterId, referenceNumber) => {
    setDownloadingPdf(letterId);
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      
      const response = await fetch(`${API_URL}/api/letters/${letterId}/download-pdf`, { headers });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Letter_${referenceNumber}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('PDF downloaded successfully');
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to download PDF');
      }
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPdf(null);
    }
  };

  const handleMarkSent = async (letterId) => {
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      
      const response = await fetch(`${API_URL}/api/letters/${letterId}/mark-sent`, {
        method: 'PUT',
        headers
      });

      if (response.ok) {
        toast.success('Letter marked as sent');
        await fetchLetters();
      } else {
        toast.error('Failed to mark letter as sent');
      }
    } catch (error) {
      toast.error('Network error');
    }
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      'Low': 'bg-gray-100 text-gray-800',
      'Normal': 'bg-blue-100 text-blue-800',
      'High': 'bg-yellow-100 text-yellow-800',
      'Urgent': 'bg-red-100 text-red-800'
    };
    return <Badge className={colors[priority] || 'bg-gray-100'}>{priority}</Badge>;
  };

  const getDirectionIcon = (direction) => {
    return direction === 'Incoming' 
      ? <ArrowDownLeft className="h-4 w-4 text-green-600" />
      : <ArrowUpRight className="h-4 w-4 text-blue-600" />;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
  }

  const incomingCount = letters.filter(l => l.direction === 'Incoming').length;
  const outgoingCount = letters.filter(l => l.direction === 'Outgoing').length;
  const overdueCount = letters.filter(l => l.is_overdue).length;

  return (
    <div className="space-y-6" data-testid="letters-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Letters Ledger</h1>
          <p className="text-gray-600">Manage incoming and outgoing correspondence</p>
        </div>
        <div className="flex gap-2">
          <Button 
            disabled
            className="bg-blue-600 hover:bg-blue-700 opacity-50 cursor-not-allowed"
            data-testid="compose-letter-button"
            title="Letter templates are being updated"
          >
            <PenLine className="h-4 w-4 mr-2" />
            Compose Letter
          </Button>
          <Button variant="outline" onClick={() => { resetForm(); setEditLetter(null); setShowModal(true); }} data-testid="add-letter-button">
            <Plus className="h-4 w-4 mr-2" />
            Log Letter
          </Button>
        </div>
      </div>

      {/* Overdue Alert */}
      {overdueCount > 0 && (
        <Alert className="border-red-200 bg-red-50">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">
            <span className="font-semibold">{overdueCount} letter(s)</span> are overdue for response
          </AlertDescription>
        </Alert>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-blue-100 rounded-lg"><Mail className="h-6 w-6 text-blue-600" /></div>
              <div><p className="text-sm text-gray-600">Total Letters</p><p className="text-2xl font-bold">{letters.length}</p></div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-green-100 rounded-lg"><ArrowDownLeft className="h-6 w-6 text-green-600" /></div>
              <div><p className="text-sm text-gray-600">Incoming</p><p className="text-2xl font-bold">{incomingCount}</p></div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-purple-100 rounded-lg"><ArrowUpRight className="h-6 w-6 text-purple-600" /></div>
              <div><p className="text-sm text-gray-600">Outgoing</p><p className="text-2xl font-bold">{outgoingCount}</p></div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-yellow-100 rounded-lg"><Clock className="h-6 w-6 text-yellow-600" /></div>
              <div><p className="text-sm text-gray-600">Pending Response</p><p className="text-2xl font-bold">{pendingLetters.length}</p></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">All Letters</TabsTrigger>
          <TabsTrigger value="pending">Pending Response ({pendingLetters.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input placeholder="Search letters..." className="pl-9" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                </div>
                <Select value={directionFilter} onValueChange={setDirectionFilter}>
                  <SelectTrigger className="w-40"><SelectValue placeholder="All" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="Incoming">Incoming</SelectItem>
                    <SelectItem value="Outgoing">Outgoing</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Letter List */}
          <div className="space-y-3">
            {letters.map(letter => (
              <Card key={letter.id} className={`hover:shadow-md transition-shadow ${letter.is_overdue ? 'border-red-200' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className={`p-2 rounded-lg ${letter.direction === 'Incoming' ? 'bg-green-100' : 'bg-blue-100'}`}>
                      {getDirectionIcon(letter.direction)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="text-sm text-gray-500">{letter.reference_number}</p>
                          <h3 className="font-semibold">
                            {letter.subject}
                            {letter.attachments?.length > 0 && <Paperclip className="inline-block ml-1.5 h-3.5 w-3.5 text-purple-500" />}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {letter.direction === 'Incoming' ? `From: ${letter.sender}` : `To: ${letter.recipient}`}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {getPriorityBadge(letter.priority)}
                          {letter.is_overdue && <Badge className="bg-red-100 text-red-800">Overdue</Badge>}
                          {letter.direction === 'Outgoing' && letter.status === 'Draft' && (
                            <Badge className="bg-yellow-100 text-yellow-800">Draft</Badge>
                          )}
                          {letter.direction === 'Outgoing' && letter.status === 'Sent' && (
                            <Badge className="bg-green-100 text-green-800">Sent</Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {new Date(letter.letter_date).toLocaleDateString()}
                        </span>
                        {letter.category && <Badge variant="outline">{letter.category}</Badge>}
                        {letter.response_due_date && letter.days_until_due !== null && (
                          <span className={`flex items-center gap-1 ${letter.days_until_due < 0 ? 'text-red-600' : letter.days_until_due <= 3 ? 'text-yellow-600' : ''}`}>
                            <Clock className="h-4 w-4" />
                            {letter.days_until_due < 0 ? `${Math.abs(letter.days_until_due)} days overdue` : `${letter.days_until_due} days left`}
                          </span>
                        )}
                        {letter.written_by && (
                          <span className="flex items-center gap-1">
                            <User className="h-4 w-4" />
                            {letter.written_by}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {/* Download PDF for outgoing letters */}
                      {letter.direction === 'Outgoing' && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleDownloadPdf(letter.id, letter.reference_number)}
                          disabled={downloadingPdf === letter.id}
                          className="text-blue-600"
                          title="Download PDF"
                        >
                          {downloadingPdf === letter.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Download className="h-4 w-4" />
                          )}
                        </Button>
                      )}
                      {/* Mark as Sent for draft outgoing letters */}
                      {letter.direction === 'Outgoing' && letter.status === 'Draft' && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleMarkSent(letter.id)}
                          className="text-green-600"
                          title="Mark as Sent"
                        >
                          <Send className="h-4 w-4" />
                        </Button>
                      )}
                      {/* Attachment download */}
                      {letter.attachments?.length > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-purple-600"
                          title="View Attachment"
                          onClick={() => window.open(`${API_URL}${letter.attachments[0]}`, '_blank')}
                          data-testid={`letter-attachment-download-${letter.id}`}
                        >
                          <Paperclip className="h-4 w-4" />
                        </Button>
                      )}
                      {/* Edit button - use compose modal for composed outgoing letters (with content/salutation), log modal for logged letters */}
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => {
                          // Composed letters have content/salutation fields
                          const isComposedLetter = letter.direction === 'Outgoing' && (letter.content || letter.salutation);
                          if (isComposedLetter) {
                            openEditComposeModal(letter);
                          } else {
                            openEditModal(letter);
                          }
                        }}
                        title="Edit Letter"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleDelete(letter.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="pending" className="space-y-3">
          {pendingLetters.map(letter => (
            <Card key={letter.id} className={`${letter.is_overdue ? 'border-red-200' : 'border-yellow-200'}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm text-gray-500">{letter.reference_number}</p>
                    <h3 className="font-semibold">{letter.subject}</h3>
                    <p className="text-sm text-gray-600">From: {letter.sender}</p>
                    <div className="flex items-center gap-2 mt-2">
                      {getPriorityBadge(letter.priority)}
                      <span className={`text-sm ${letter.is_overdue ? 'text-red-600 font-semibold' : 'text-yellow-600'}`}>
                        {letter.is_overdue ? `${Math.abs(letter.days_until_due)} days overdue` : `Due in ${letter.days_until_due} days`}
                      </span>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => handleMarkResponded(letter.id)}>
                    Mark Responded
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {pendingLetters.length === 0 && (
            <Card><CardContent className="p-8 text-center text-gray-500">No pending responses</CardContent></Card>
          )}
        </TabsContent>
      </Tabs>

      {letters.length === 0 && activeTab === 'all' && (
        <Card>
          <CardContent className="p-12 text-center">
            <Mail className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No letters yet</h3>
            <Button onClick={() => setShowModal(true)}><Plus className="h-4 w-4 mr-2" />Log Letter</Button>
          </CardContent>
        </Card>
      )}

      {/* Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg" data-testid="letter-modal">
          <DialogHeader>
            <DialogTitle>{editLetter ? 'Edit Letter' : 'New Letter'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto">
            {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Reference Number *</Label>
                <Input value={formData.reference_number} onChange={(e) => setFormData({...formData, reference_number: e.target.value})} required disabled={!!editLetter} />
              </div>
              <div className="space-y-2">
                <Label>Direction *</Label>
                <Select value={formData.direction} onValueChange={(v) => setFormData({...formData, direction: v})} disabled={!!editLetter}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Incoming">Incoming</SelectItem>
                    <SelectItem value="Outgoing">Outgoing</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Subject *</Label>
              <Input value={formData.subject} onChange={(e) => setFormData({...formData, subject: e.target.value})} required />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Sender *</Label>
                <Input value={formData.sender} onChange={(e) => setFormData({...formData, sender: e.target.value})} required />
              </div>
              <div className="space-y-2">
                <Label>Recipient *</Label>
                <Input value={formData.recipient} onChange={(e) => setFormData({...formData, recipient: e.target.value})} required />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Letter Date *</Label>
                <Input type="date" value={formData.letter_date} onChange={(e) => setFormData({...formData, letter_date: e.target.value})} required />
              </div>
              <div className="space-y-2">
                <Label>Response Due Date</Label>
                <Input type="date" value={formData.response_due_date} onChange={(e) => setFormData({...formData, response_due_date: e.target.value})} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={formData.priority} onValueChange={(v) => setFormData({...formData, priority: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Summary</Label>
              <Textarea value={formData.summary} onChange={(e) => setFormData({...formData, summary: e.target.value})} rows={3} />
            </div>

            {/* Attachment (optional) */}
            <div className="space-y-2">
              <Label>Attachment (optional)</Label>
              <div className="flex items-center gap-3">
                <Input
                  type="file"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={(e) => setAttachmentFile(e.target.files?.[0] || null)}
                  className="flex-1"
                  data-testid="letter-attachment-input"
                />
                {attachmentFile && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => { setAttachmentFile(null); }}
                    className="text-gray-400 hover:text-red-500"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <p className="text-xs text-gray-500">PDF, DOC, DOCX, JPG, PNG — Max 10MB</p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                {editLetter ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Compose Letter Modal */}
      <Dialog open={showComposeModal} onOpenChange={(open) => {
        setShowComposeModal(open);
        if (!open) resetComposeForm();
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden" data-testid="compose-letter-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenLine className="h-5 w-5 text-blue-600" />
              {editingComposedLetter ? 'Edit Letter' : 'Compose Letter'}
            </DialogTitle>
            <DialogDescription>
              {editingComposedLetter 
                ? `Editing letter: ${editingComposedLetter.reference_number}` 
                : 'Create a professional letter on company letterhead. Reference number will be auto-generated.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleComposeLetter} className="space-y-4 overflow-y-auto max-h-[65vh] pr-2">
            {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
            
            {/* Date */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input 
                  type="date" 
                  value={composeData.letter_date} 
                  onChange={(e) => setComposeData({...composeData, letter_date: e.target.value})} 
                  required 
                />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={composeData.category} onValueChange={(v) => setComposeData({...composeData, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Recipient */}
            <div className="space-y-2">
              <Label>Recipient Name *</Label>
              <Input 
                value={composeData.recipient_name} 
                onChange={(e) => setComposeData({...composeData, recipient_name: e.target.value})} 
                placeholder="e.g., The Manager, ABC Company"
                required 
              />
            </div>

            <div className="space-y-2">
              <Label>Recipient Address *</Label>
              <Textarea 
                value={composeData.recipient_address} 
                onChange={(e) => setComposeData({...composeData, recipient_address: e.target.value})} 
                placeholder="Enter the full postal address (each line on a new line)"
                rows={3}
                required 
              />
            </div>

            {/* Salutation */}
            <div className="space-y-2">
              <Label>Salutation *</Label>
              <Input 
                value={composeData.salutation} 
                onChange={(e) => setComposeData({...composeData, salutation: e.target.value})} 
                placeholder="e.g., Dear Sir, Dear John, Dear Mr. Smith"
                required 
              />
            </div>

            {/* Subject */}
            <div className="space-y-2">
              <Label>Subject *</Label>
              <Input 
                value={composeData.subject} 
                onChange={(e) => setComposeData({...composeData, subject: e.target.value})} 
                placeholder="Brief subject of the letter"
                required 
              />
            </div>

            {/* Main Content - Rich Text Editor */}
            <div className="space-y-2">
              <Label>Main Message *</Label>
              <div className="border rounded-md overflow-hidden">
                <ReactQuill 
                  theme="snow"
                  value={composeData.content} 
                  onChange={(value) => setComposeData({...composeData, content: value})}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Write your letter content here. Use the toolbar for formatting (bold, italic, lists, etc.)"
                  style={{ minHeight: '200px' }}
                />
              </div>
              <p className="text-xs text-gray-500">
                Use the toolbar to format text: <strong>Bold</strong>, <em>Italic</em>, numbered lists, bullet points, etc.
              </p>
            </div>

            {/* Closing */}
            <div className="space-y-2">
              <Label>Closing Line</Label>
              <Select value={composeData.closing_line} onValueChange={(v) => setComposeData({...composeData, closing_line: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CLOSINGS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Author */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Written By *</Label>
                <Input 
                  value={composeData.written_by} 
                  onChange={(e) => setComposeData({...composeData, written_by: e.target.value})} 
                  placeholder="Your full name"
                  required 
                />
              </div>
              <div className="space-y-2">
                <Label>Title/Position *</Label>
                <Input 
                  value={composeData.writer_title} 
                  onChange={(e) => setComposeData({...composeData, writer_title: e.target.value})} 
                  placeholder="e.g., HR Manager"
                  required 
                />
              </div>
            </div>

            {/* Priority */}
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={composeData.priority} onValueChange={(v) => setComposeData({...composeData, priority: v})}>
                <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <DialogFooter className="pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => {
                setShowComposeModal(false);
                resetComposeForm();
              }}>Cancel</Button>
              <Button type="submit" disabled={submitting} className="bg-blue-600 hover:bg-blue-700">
                {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
                {editingComposedLetter ? 'Save Changes' : 'Create Letter'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LettersLedger;
